import { store } from 'createStore';
import type { SimulationResultInterface } from 'store/simulation/types';
import type { UnitInfoDataInterface } from 'store/unitInfoData';
import * as StorageConfig from 'unitData/Storage/StorageData';
import {
  CONVERSIONS,
  convertToCurrentMetric,
  getMetricUnit,
  roundToXDecimals,
} from 'util/conversions';
import type {
  PortOutputFlowInterface,
  UnitInfoStorageInterface,
  UnitInterface,
} from '../../store/units/types';
import { addNotesTexts } from './notesTexts';
import type { UnitInfoTextInterface } from './types';
import {
  addValue,
  getMetricType,
  productSize,
  storageUnitName,
  toMetricCapacity,
} from './utils';

export const getStorageTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
  outputFlow?: PortOutputFlowInterface,
): UnitInfoTextInterface[] => {
  const rounding = store.getState().pgState.rounding;
  const unitInfo = unit.unitInfo as UnitInfoStorageInterface;

  const oversizeAmount = simulationResult?.simulationUnitInfo?.oversizeAmount;
  const moistureContent =
    outputFlow?.moisture !== undefined
      ? roundToXDecimals(outputFlow?.moisture, 1)
      : 0;
  const { displayPredictedOversize, displayMoisture } = unitInfo.display;
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const metricType = getMetricType();
  storageUnitName(unitInfo, StorageConfig.storageTypes, textArray);

  if (outputFlow) {
    if (unitInfo.display.displayTotalStoredMass) {
      let totalMass = outputFlow.totalMass;

      if (totalMass && rounding) {
        totalMass = Math.floor(Number(outputFlow?.totalMass) / 10) * 10;
      }

      textArray.push({
        text: `${convertToCurrentMetric({
          value: totalMass,
          metricType: metricType,
          conversionType: CONVERSIONS.CAPACITY,
        })} ${getMetricUnit(metricType, CONVERSIONS.CAPACITY)}`,
      });
    }
    productSize(unitInfo, outputFlow, textArray);

    add(
      `(Predicted Oversize: ${oversizeAmount} %)`,
      !!displayPredictedOversize && oversizeAmount !== undefined,
    );
  }
  add(`Moisture: ${moistureContent}% (by weight)`, displayMoisture);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getStorageWith1OutputTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
  outputFlow?: PortOutputFlowInterface,
): UnitInfoTextInterface[] => {
  const unitData = unitInfoData.PAN_FEEDER_SP;
  const unitInfo = unit.unitInfo as UnitInfoStorageInterface;
  const loadRight = simulationResult?.simulationUnitInfo?.loadRight;
  const autoSelectFeederRight =
    simulationResult?.simulationUnitInfo?.feederModelRight;
  const moistureContent =
    outputFlow?.moisture !== undefined
      ? roundToXDecimals(outputFlow?.moisture, 1)
      : 0;
  const textArray: UnitInfoTextInterface[] = [];
  const { displayTotalStoredMass, displayMoisture } = unitInfo.display;
  const add = addValue(textArray);

  storageUnitName(unitInfo, StorageConfig.storageWith1OutPutTypes, textArray);
  add(
    'Used as high bin',
    unitInfo.useAsHighBin && !!unitInfo.display.displayHighBin,
  );

  if (outputFlow) {
    if (displayTotalStoredMass) {
      const totalMass = outputFlow.totalMass;
      const outgoingMass =
        unitInfo.feederOutputOptions === 'PERCENT'
          ? totalMass * (unitInfo.amountRight / 100)
          : unitInfo.amountRight;
      const remainingMass = totalMass - outgoingMass;

      add(toMetricCapacity('Flow rate:', totalMass), unitInfo.feedInOut);
      add(
        toMetricCapacity('Total incoming mass:', totalMass),
        !unitInfo.feedInOut,
      );
      add(
        toMetricCapacity('Total outgoing mass:', outgoingMass),
        !unitInfo.feedInOut,
      );
      add(
        toMetricCapacity('Total remaining mass:', remainingMass),
        !unitInfo.feedInOut,
      );
    }

    productSize(unitInfo, outputFlow, textArray);
  }

  add(`Moisture: ${moistureContent}% (by weight)`, displayMoisture);

  if (unitInfo.display.displayFeederModel) {
    if (unitInfo.otherModelRight) {
      if (unitInfo.otherRightPanFeederModelName) {
        textArray.push({
          text: `Feeder: ${unitInfo.otherRightPanFeederModelName}`,
        });
      }
    } else
      textArray.push({
        text:
          unitInfo.autoSelect &&
          autoSelectFeederRight !== undefined &&
          autoSelectFeederRight !== unitInfo.modelName
            ? `Feeder: ${unitData[autoSelectFeederRight]?.descriptiveName}`
            : `Feeder: ${
                unitData[unitInfo.rightPanFeederModelName]?.descriptiveName
              }`,
      });
  }
  if (unitInfo.display.displayLoad && typeof loadRight === 'number') {
    if (store.getState().settingsState?.unitDisplay?.showLoad) {
      textArray.push({ text: `Load: ${loadRight} %` });
    }
  }
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getStorageWith2OutputTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
  outputFlow?: PortOutputFlowInterface,
): UnitInfoTextInterface[] => {
  const unitdata = unitInfoData.PAN_FEEDER_SP;
  const unitInfo = unit.unitInfo as UnitInfoStorageInterface;
  const loadRight = simulationResult?.simulationUnitInfo?.loadRight;
  const loadLeft = simulationResult?.simulationUnitInfo?.loadLeft;
  const autoSelectFeeder =
    simulationResult?.simulationUnitInfo?.feederModelRight;
  const moistureContent =
    outputFlow?.moisture !== undefined
      ? roundToXDecimals(outputFlow?.moisture, 1)
      : 0;
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const { feedInOut } = unitInfo;

  storageUnitName(unitInfo, StorageConfig.storageWith2OutPutTypes, textArray);
  add(
    'Used as high bin',
    unitInfo.useAsHighBin && !!unitInfo.display.displayHighBin,
  );

  if (outputFlow) {
    if (unitInfo.display.displayTotalStoredMass) {
      const totalMass = outputFlow.totalMass;
      const outgoingRightMass =
        unitInfo.feederOutputOptions === 'PERCENT'
          ? totalMass * (unitInfo.amountRight / 100)
          : unitInfo.amountRight;
      const outgoingLeftMass =
        unitInfo.feederOutputOptions === 'PERCENT'
          ? totalMass * (unitInfo.amountLeft / 100)
          : unitInfo.amountLeft;
      const remainingMass = totalMass - outgoingRightMass - outgoingLeftMass;

      add(toMetricCapacity('Flow rate:', totalMass), unitInfo.feedInOut);
      add(toMetricCapacity('Total incoming mass:', totalMass), !feedInOut);
      add(
        toMetricCapacity('Total outgoing mass (right):', outgoingRightMass),
        !feedInOut,
      );
      add(
        toMetricCapacity('Total outgoing mass (left):', outgoingLeftMass),
        !feedInOut,
      );
      add(toMetricCapacity('Total remaining mass:', remainingMass), !feedInOut);
    }
    productSize(unitInfo, outputFlow, textArray);
  }

  add(
    `Moisture: ${moistureContent}% (by weight)`,
    unitInfo.display.displayMoisture,
  );

  if (unitInfo.display.displayFeederModel) {
    add(
      `Right Feeder: ${unitInfo.otherRightPanFeederModelName}`,
      !!unitInfo.otherRightPanFeederModelName && unitInfo.otherModelRight,
    );
    add(
      unitInfo.autoSelect &&
        autoSelectFeeder !== undefined &&
        autoSelectFeeder !== unitInfo.modelName
        ? `Right Feeder: ${unitdata[autoSelectFeeder]?.descriptiveName}`
        : `Right Feeder: ${
            unitdata[unitInfo.rightPanFeederModelName]?.descriptiveName
          }`,
      !unitInfo.otherModelRight,
    );
    add(
      `Left Feeder: ${unitInfo.otherLeftPanFeederModelName}`,
      unitInfo.otherModelLeft && !!unitInfo.otherLeftPanFeederModelName,
    );
    add(
      unitInfo.autoSelect &&
        autoSelectFeeder !== undefined &&
        autoSelectFeeder !== unitInfo.modelName
        ? `Left Feeder: ${unitdata[autoSelectFeeder]?.descriptiveName}`
        : `Left Feeder: ${
            unitdata[unitInfo.leftPanFeederModelName]?.descriptiveName
          }`,
      !unitInfo.otherModelLeft,
    );
  }

  if (
    unitInfo.display.displayLoad &&
    store.getState().settingsState?.unitDisplay?.showLoad
  ) {
    add(`Right Load: ${loadRight} %`, typeof loadRight === 'number');
    add(`Left Load: ${loadLeft} %`, typeof loadLeft === 'number');
  }

  addNotesTexts({ unitInfo, textArray });

  return textArray;
};
